import { useQuery, keepPreviousData } from "@tanstack/react-query";
import useSystemStore from "@store/useSystemStore";
import companyService from "@services/company.service";
import { COMPANIES_QUERY_KEY, QUERY_CACHE_DEFAULT_EXPIRATION_MS } from "@config/query";
import { isValidUrl } from "@utils/strings.util";

interface UseCompaniesProps {
  searchQuery: string;
  currentCompanies: string[];
  themeId?: string;
  subType?: string;
}

export function useSearchCompanies({
  searchQuery,
  currentCompanies,
  themeId,
  subType,
}: UseCompaniesProps) {
  const { isAppActive } = useSystemStore();

  const { data, error, isLoading, fetchStatus, isPlaceholderData, refetch } = useQuery({
    enabled: isAppActive,
    queryKey: [COMPANIES_QUERY_KEY, searchQuery, currentCompanies, themeId, subType],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchQuery) {
        params.append(
          "search",
          (isValidUrl(searchQuery)
            ? searchQuery.replace(/^(https?:\/\/)?(www\.)?/, "").replace(/\/$/, "")
            : searchQuery) || ""
        );
      }
      params.append("ordering", "title");
      params.append(
        "query",
        "{id,title,website,logo_url,active_deal,type,themes,platform_company,priority,detail_field_values,description,enriched_detail_field_values,enriched_fields}"
      );
      if (themeId) params.append("deal_theme", `-${themeId}`);
      if (subType) params.append("subtype", subType);
      return await companyService.getCompanies(params);
    },
    select: (data) => {
      return data?.results?.filter((result) => !currentCompanies.includes(result.id)) ?? [];
    },
    placeholderData: keepPreviousData,
    staleTime: QUERY_CACHE_DEFAULT_EXPIRATION_MS,
    retry: false,
  });

  return { data, error, isLoading, fetchStatus, isPlaceholderData, refetch };
}
